import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  CssBaseline,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import { getPricingHolidayByIdUseCase } from "../domain/usecases/getPricingHolidayByIdUseCase";
import { updatePricingHolidayUseCase } from "../domain/usecases/updatePricingHolidayUseCase";
import Topbar from "../../../components/topbar/TopBar";
import { getCountryListUseCase } from "../../geography_catalog/country/domain/usecases/getCountryListUseCase";
import { getDepartmentListByCountryIdUseCase } from "../../geography_catalog/department/domain/usecases/getDepartmentListByCountryIdUseCase";
import { getProvinceListByDepartmentIdUseCase } from "../../geography_catalog/province/domain/usecases/getProvinceListByDepartmentIdUseCase";

function formatDateForInput(value) {
  if (!value) {
    return "";
  }

  const parts = value.split("-");

  if (parts.length !== 3) {
    return value;
  }

  if (parts[0].length === 4) {
    return value;
  }

  const [day, month, year] = parts;
  return `${year}-${month}-${day}`;
}

function formatDateForPayload(value) {
  if (!value) {
    return "";
  }

  const [year = "", month = "", day = ""] = value.split("-");
  return `${day}-${month}-${year}`;
}

function resolveSelectValue(value, options = []) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const normalizedValue = String(value);
  const matched = options.find(
    (item) =>
      String(item.id) === normalizedValue ||
      String(item.code || "").toUpperCase() === normalizedValue.toUpperCase() ||
      String(item.name || "").toUpperCase() === normalizedValue.toUpperCase(),
  );

  return matched ? String(matched.id) : normalizedValue;
}

function createForm(holiday, countries = [], departments = [], provinces = []) {
  return {
    id: holiday?.id || "",
    name: holiday?.name || "",
    holidayDate: formatDateForInput(holiday?.holidayDate || ""),
    countryId: resolveSelectValue(
      holiday?.countryId ?? holiday?.country,
      countries,
    ),
    departmentId: resolveSelectValue(
      holiday?.departmentId ?? holiday?.department,
      departments,
    ),
    provinceId: resolveSelectValue(
      holiday?.provinceId ?? holiday?.province,
      provinces,
    ),
    multiplier:
      holiday?.multiplier === null || holiday?.multiplier === undefined
        ? "1.0"
        : String(holiday.multiplier),
    active: Boolean(holiday?.active),
  };
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return message.includes("success") || message.includes("updated");
}

export default function PricingHolidayUpdatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(createForm());
  const [countries, setCountries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [countryRecords, holiday] = await Promise.all([
          getCountryListUseCase(),
          getPricingHolidayByIdUseCase(id),
        ]);

        const holidayForm = createForm(holiday, countryRecords);
        setCountries(countryRecords);
        setForm(holidayForm);
      } catch (error) {
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la fecha festiva.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [id]);

  useEffect(() => {
    if (!form.countryId) {
      setDepartments([]);
      setProvinces([]);
      return;
    }

    const loadDepartments = async () => {
      try {
        const records = await getDepartmentListByCountryIdUseCase(
          form.countryId,
        );
        setDepartments(records);

        const hasSelectedDepartment = records.some(
          (department) => String(department.id) === String(form.departmentId),
        );

        if (!hasSelectedDepartment) {
          setForm((prev) => ({
            ...prev,
            departmentId: "",
            provinceId: "",
          }));
        }
      } catch (error) {
        setDepartments([]);
        setProvinces([]);
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la lista de departamentos.",
        });
      }
    };

    loadDepartments();
  }, [form.countryId]);

  useEffect(() => {
    if (!form.departmentId) {
      setProvinces([]);
      return;
    }

    const loadProvinces = async () => {
      try {
        const records = await getProvinceListByDepartmentIdUseCase(
          form.departmentId,
        );
        setProvinces(records);

        const hasSelectedProvince = records.some(
          (province) => String(province.id) === String(form.provinceId),
        );

        if (!hasSelectedProvince) {
          setForm((prev) => ({
            ...prev,
            provinceId: "",
          }));
        }
      } catch (error) {
        setProvinces([]);
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la lista de provincias.",
        });
      }
    };

    loadProvinces();
  }, [form.departmentId]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckChange = (event) => {
    setForm((prev) => ({
      ...prev,
      active: event.target.checked,
    }));
  };

  const handleCountryChange = (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      countryId: value,
      departmentId: "",
      provinceId: "",
    }));
  };

  const handleDepartmentChange = (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      departmentId: value,
      provinceId: "",
    }));
  };

  const handleSave = async () => {
    const payload = {
      id: form.id,
      name: form.name.trim(),
      holidayDate: formatDateForPayload(form.holidayDate),
      countryId: Number(form.countryId),
      departmentId: form.departmentId ? Number(form.departmentId) : null,
      provinceId: form.provinceId ? Number(form.provinceId) : null,
      multiplier: Number(form.multiplier),
      active: Boolean(form.active),
    };

    try {
      setSaving(true);
      const response = await updatePricingHolidayUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error(
          response?.message || "No se pudo actualizar la fecha festiva.",
        );
      }

      setMessage({
        open: true,
        type: "success",
        text: "Fecha festiva actualizada correctamente.",
      });

      window.setTimeout(() => {
        navigate("/pricing-holiday/list");
      }, 1200);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo actualizar la fecha festiva.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="container">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": {
              color: "#000",
            },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: { xs: 2.5, md: 3 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      Actualizar Fecha Festiva
                    </Typography>
                    <Typography
                      sx={{ mt: 0.75, color: "#fff !important", opacity: 0.88 }}
                    >
                      Ajusta la fecha especial y su multiplicador de Precio.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Volver
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 2, md: 4 } }}>
                {loading ? (
                  <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">
                      Cargando fecha festiva...
                    </Typography>
                  </Stack>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      mx: "auto",
                      maxWidth: 480,
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      p: { xs: 1, md: 2 },
                    }}
                  >
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                      />
                    <TextField
                      fullWidth
                      type="date"
                      label="Fecha festiva"
                      name="holidayDate"
                      value={form.holidayDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      select
                      label="Pais"
                      name="countryId"
                      value={form.countryId}
                      onChange={handleCountryChange}
                      sx={{
                        "& .MuiInputBase-input": { color: "#fff" },
                        "& .MuiSelect-select": { color: "#fff" },
                        "& .MuiInputLabel-root": { color: "#fff" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.35)",
                        },
                        "& .MuiSelect-icon": { color: "#fff" },
                      }}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.id} value={String(country.id)}>
                          {country.name} - {country.code}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      select
                      label="Departamento"
                      name="departmentId"
                      value={form.departmentId}
                      onChange={handleDepartmentChange}
                      sx={{
                        "& .MuiInputBase-input": { color: "#fff" },
                        "& .MuiSelect-select": { color: "#fff" },
                        "& .MuiInputLabel-root": { color: "#fff" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.35)",
                        },
                        "& .MuiSelect-icon": { color: "#fff" },
                      }}
                    >
                      <MenuItem value="">
                        <em>Sin departamento</em>
                      </MenuItem>
                      {departments.map((department) => (
                        <MenuItem
                          key={department.id}
                          value={String(department.id)}
                        >
                          {department.name} - {department.code}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      select
                      label="Provincia"
                      name="provinceId"
                      value={form.provinceId}
                      onChange={handleChange}
                      sx={{
                        "& .MuiInputBase-input": { color: "#fff" },
                        "& .MuiSelect-select": { color: "#fff" },
                        "& .MuiInputLabel-root": { color: "#fff" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.35)",
                        },
                        "& .MuiSelect-icon": { color: "#fff" },
                      }}
                    >
                      <MenuItem value="">
                        <em>Sin provincia</em>
                      </MenuItem>
                      {provinces.map((province) => (
                        <MenuItem key={province.id} value={String(province.id)}>
                          {province.name} - {province.code}
                        </MenuItem>
                      ))}
                    </TextField>
                      <TextField
                        fullWidth
                        type="number"
                        label="Multiplicador"
                        name="multiplier"
                        value={form.multiplier}
                        onChange={handleChange}
                        inputProps={{ step: "0.1", min: "1" }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.active}
                            onChange={handleCheckChange}
                            sx={{
                              color: "#165c53",
                              "&.Mui-checked": {
                                color: "#165c53",
                              },
                            }}
                          />
                        }
                        label="Activo"
                        sx={{
                          mx: 0,
                          "& .MuiFormControlLabel-label": {
                            color: "#fff",
                            fontWeight: 600,
                          },
                        }}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                          mt: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          bgcolor: "#165c53",
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "#12483f",
                          },
                        }}
                      >
                        {saving ? "Actualizando..." : "Actualizar"}
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
}
